import React from 'react';
import { BreadcrumbSchema, FAQSchema, ArticleSchema } from '@/components/JsonLd';
import {
  RichHero, TableOfContents, ProseSection, AnswerBox, FactGrid,
  ComparisonTable, ProsCons, RichFAQ, RelatedLinks, RichCTA, ChargerCard,
} from '../RichBlocks';
import { ENKEL_PRODUCTS } from '@/components/seo/content/ContentRegistry';
import SEODisclaimer from '@/components/seo/content/SEODisclaimer';

/**
 * Easee Charge Lite vs Easee Home — bespoke rich comparison.
 * Sources: Easee official product specs (easee.com). No competitor electricity pricing.
 * enkel does not sell Easee, but Saving Mode + laderabat are compatible.
 */
export default function EaseeChargeLiteVsHome({ page }) {
  const updated = new Date(page?.last_data_refresh || page?.updated_date || Date.now())
    .toLocaleDateString('da-DK', { day: 'numeric', month: 'long', year: 'numeric' });

  const faqs = [
    {
      question: 'Hvad er forskellen på Easee Charge Lite og Easee Home?',
      answer: 'Easee Home var den oprindelige model. Easee Charge Lite er en nyere, forenklet udgave bygget på samme platform som Easee Charge, men uden enkelte premium-funktioner. Begge er kompakte Type 2-ladebokse med op til 22 kW. Charge Lite er typisk det billigere og mere moderne valg, da Home efterhånden udfases.',
    },
    {
      question: 'Kan jeg bruge en Easee med enkel energi?',
      answer: 'Ja. enkel energi sælger ikke Easee, men Saving Mode og laderabat (10 øre/kWh) er kompatible med Easee Home, Charge, Charge Lite og Peak. Du tilslutter blot din eksisterende Easee til enkel-appen og får alle fordelene uden at købe ny hardware.',
    },
    {
      question: 'Er der forskel på ladehastigheden?',
      answer: 'Nej. Både Easee Home og Charge Lite leverer op til 22 kW på en 3-faset installation. Den reelle hastighed afhænger af din bil og din elinstallation – ikke af modellen.',
    },
    {
      question: 'Bør jeg vælge Easee eller en ladeboks fra enkel?',
      answer: `Har du allerede en Easee, er der ingen grund til at skifte – tilslut den til Saving Mode. Skal du købe ny, anbefaler vi NexBlue Edge 2 (${ENKEL_PRODUCTS.chargers.nexblue_edge_2.price.toLocaleString('da-DK')} kr.) eller Zaptec Go (${ENKEL_PRODUCTS.chargers.zaptec_go.price.toLocaleString('da-DK')} kr.) gennem enkel, hvor Saving Mode, serviceaftale og laderabat er inkluderet til 0 kr./md.`,
    },
    {
      question: 'Var Easee ikke ramt af et salgsstop?',
      answer: 'Easee fik i 2023 et midlertidigt salgsstop i Sverige efter en sikkerhedsvurdering fra de svenske myndigheder. Sagen blev senere afklaret, og Easee opdaterede sine produkter. Det berørte primært det svenske marked. Eksisterende Easee-bokse i Danmark kan fortsat bruges med enkel Saving Mode.',
    },
  ];

  const toc = [
    { id: 'svar', label: 'Kort svar' },
    { id: 'tabel', label: 'Side-om-side' },
    { id: 'enkel', label: 'Brug din Easee' },
    { id: 'alternativer', label: 'Alternativer' },
    { id: 'faq', label: 'FAQ' },
  ];

  return (
    <article itemScope itemType="https://schema.org/Article">
      <BreadcrumbSchema items={[{ name: 'Forside', path: '/' }, { name: 'Ladebokse', path: '/opladere' }, { name: 'Easee Charge Lite vs Home', path: `/sammenlign/${page.slug}` }]} />
      <FAQSchema faqs={faqs} />
      <ArticleSchema headline="Easee Charge Lite vs Easee Home" description={page?.meta_description} datePublished={page?.created_date} dateModified={page?.last_data_refresh || page?.updated_date} />

      <RichHero
        eyebrow="Sammenligning · Ladebokse · 2026"
        h1="Easee Charge Lite vs Easee Home: Hvad er forskellen?"
        intro="To Easee-hjemmeladere stillet op mod hinanden. Vi forklarer forskellene – og hvordan du får Saving Mode og laderabat på din Easee, uanset model."
        badges={['Begge op til 22 kW', 'Type 2-stik', 'Virker med enkel Saving Mode']}
        updated={updated}
        image="https://images.unsplash.com/photo-1558389186-438424b00a6b?auto=format&fit=crop&w=1600&q=70"
        kpis={[
          { value: '22 kW', label: 'begge modeller' },
          { value: 'Type 2', label: 'passer alle elbiler' },
          { value: '10 øre/kWh', label: 'laderabat med enkel' },
          { value: '0 kr./md.', label: 'Saving Mode + service' },
        ]}
      />
      <TableOfContents items={toc} />

      <ProseSection id="svar" heading="Charge Lite eller Home – kort svar">
        <AnswerBox question="Hvad er forskellen?">
          <strong>Easee Home</strong> er den oprindelige model, der efterhånden udfases. <strong>Easee Charge Lite</strong> er en nyere, forenklet
          udgave på samme platform som Easee Charge. Begge er kompakte <strong>22 kW</strong> Type 2-ladebokse – ladehastigheden er den samme.
          Charge Lite er typisk det mere moderne og billigere valg i dag.
        </AnswerBox>
        <p>
          Vigtigst: enkel energi sælger ikke Easee, men <strong>Saving Mode og laderabat virker med begge modeller</strong>. Har du en Easee,
          kan du tilslutte den til enkel-appen og automatisk lade i de billigste timer på Nord Pool.
        </p>
      </ProseSection>

      <ProseSection id="tabel" heading="Easee Charge Lite vs Home – side om side" alt>
        <div className="my-2">
          <ComparisonTable
            caption="Sammenligning af Easee Charge Lite og Easee Home"
            columns={[{ name: 'Easee Charge Lite' }, { name: 'Easee Home' }]}
            rows={[
              { label: 'Status', values: ['Nyere model', 'Udfases'] },
              { label: 'Max effekt', values: ['22 kW', '22 kW'] },
              { label: 'Stiktype', values: ['Type 2', 'Type 2'] },
              { label: 'Forbindelse', values: ['Wi-Fi', 'Wi-Fi + 4G'] },
              { label: 'Load balancing', values: [true, true] },
              { label: 'App-styring', values: [true, true] },
              { label: 'Saving Mode-kompatibel', values: [true, true] },
              { label: 'Laderabat 10 øre/kWh', values: [true, true] },
            ]}
          />
        </div>
        <p className="text-xs text-gray-500 mt-3">Kilder: Easee officielle produktspecifikationer (easee.com).</p>
      </ProseSection>

      <ProseSection id="enkel" heading="Brug din Easee med enkel – uden ny hardware">
        <p>
          Uanset om du har en Easee Home eller Charge Lite, kan du koble den til enkel Saving Mode. Du får 10 øre/kWh i laderabat, automatisk
          opladning i de billigste timer, serviceaftale og elafgift-refusion – alt til 0 kr./md. oveni din el-aftale. Du betaler spotpris uden
          tillæg fra 18 kr./md. uden binding.
        </p>
        <FactGrid columns={3} facts={[
          { value: 'Op til ~30%', label: 'Saving Mode-besparelse', sub: 'Historisk 15–30% (ikke garanteret)' },
          { value: '10 øre/kWh', label: 'Laderabat', sub: 'Kun via Saving Mode' },
          { value: '0 kr.', label: 'At skifte elselskab', sub: 'Ingen binding eller gebyr' },
        ]} />
      </ProseSection>

      <div id="alternativer" className="container mx-auto px-6 py-10">
        <h2 className="text-2xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Skal du købe ny? Vores anbefalinger</h2>
        <div className="grid sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
          <ChargerCard charger={ENKEL_PRODUCTS.chargers.nexblue_edge_2} note="Bedst til prisen · V2G" />
          <ChargerCard charger={ENKEL_PRODUCTS.chargers.zaptec_go} note="Mest pålidelig" />
        </div>
      </div>

      <section className="py-12 bg-[#F6F3EC]">
        <div className="container mx-auto px-6">
          <h2 id="faq" className="text-2xl md:text-3xl font-bold text-center mb-8 text-[#1F2E33]" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>Ofte stillede spørgsmål</h2>
          <RichFAQ faqs={faqs} />
        </div>
      </section>

      <RelatedLinks links={[
        { path: '/sammenlign/zaptec-vs-easee', label: 'Zaptec vs Easee' },
        { path: '/nexblue-edge-2', label: 'NexBlue Edge 2 – produktside' },
        { path: '/zaptec-go', label: 'Zaptec Go – produktside' },
        { path: '/hvilken-ladeboks-skal-jeg-vaelge', label: 'Hvilken ladeboks skal jeg vælge?' },
        { path: '/opladere', label: 'Alle ladebokse' },
        { path: '/el-fordele#laderabat', label: 'Sådan virker laderabat' },
      ]} />

      <RichCTA
        headline="Har du en Easee? Få Saving Mode gratis"
        subtext="Tilslut din Easee til enkel-appen og få laderabat, Saving Mode og serviceaftale til 0 kr./md."
        primary={{ path: '/elaftale', text: 'Se el-aftaler' }}
        secondary={{ path: '/opladere', text: 'Se ladebokse' }}
      />
      <SEODisclaimer page={page} />
    </article>
  );
}